/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {InputDisplayModeEnum} from '../../shared/input/input-display-mode.enum';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {
    Observable,
    of,
    Subject,
    Subscription
} from 'rxjs';
import {
    PartNumberChangesSolutionConfigurationModel
} from '../services/part-number-changes-detail-configuration/models/pnc-trial/part-number-changes-solution-configuration.model';
import {
    PartNumberChangesDetailTrialDisplayModeEnum
} from '../services/part-number-changes-detail-configuration/models/pnc-trial/part-number-changes-detail-trial-display-mode.enum';
import {WarningsEnum} from '../../shared/warnings/warnings.enum';
import {RouteSearchCriteriaModel} from '../../api/ms-partnumberchanges/contracts/route-search-criteria.model';
import {PartNumberChangesAiSolutionState} from './part-number-changes-ai-solution.state';
import {TypeaheadHelperService} from '../../shared/services/typeahead-helper.service';
import {
    ValidatorFn,
    Validators
} from '@angular/forms';
import {objectsValidator} from '../../shared/input/input-validators';
import {MaterialApi} from '../../api/ms-partnumberchanges/material.api';
import {InputTextComponent} from '../../shared/input/input-text/input-text.component';
import {Clipboard} from '@angular/cdk/clipboard';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {InputDropdownComponent} from '../../shared/input/input-dropdown/input-dropdown.component';
import {SpinnerService} from '../../shared/spinner/spinner.service';
import {
    MaterialDetailContract
} from '../../api/ms-partnumberchanges/contracts/material-detail/model/material-detail.contract';
import {PartNumberChangesUpdateAiMaterialModel} from './models/part-number-changes-update-ai-material.model';
import {map} from 'rxjs/operators';
import {MaterialDetailMapper} from '../models/material-detail/material-detail.mapper';

@Component({
    selector: 'app-part-number-changes-ai-solution',
    templateUrl: './part-number-changes-ai-solution.component.html',
    styleUrls: [
        '../styles/pnc-base.component.css',
        './part-number-changes-ai-solution.component.css',
        '../styles/pnc-base-solution.component.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartNumberChangesAiSolutionComponent
        extends StatefulComponent<PartNumberChangesAiSolutionState>
        implements OnInit, AfterViewInit {

    @Input() supplyingPackingPlantCode: string;
    @Input() updateRouteSearchCriteriaChange = new Subject<RouteSearchCriteriaModel>();
    @Input() updatedOldPartNumberMaterial = new Subject<PartNumberChangesUpdateAiMaterialModel>();
    @Input() triggerFormReset: Subject<void> = new Subject<void>();
    @Input() configuration: PartNumberChangesSolutionConfigurationModel;
    @Input() isLoading: (loading: boolean) => void;

    @Output() solution: Subject<PartNumberChangesAiSolutionState> =
            new Subject<PartNumberChangesAiSolutionState>();

    @ViewChild('materialInput') materialInput: InputDropdownComponent;
    @ViewChild('quantityInput') quantityInput: InputTextComponent;

    private readonly quantityRegex = /^[1-9]{1}[0-9]{0,5}$/;
    validators: { [key: string]: ValidatorFn[] } = {
        material: [objectsValidator],
        quantity: [Validators.maxLength(6), Validators.pattern(this.quantityRegex)]
    };
    materialMessages = new Map<WarningsEnum, string[]>();
    quantityMessages = new Map<WarningsEnum, string[]>();
    public readonly newPartNumberAiLabel = 'New part number AI';
    public readonly newPartNumberAiPlaceholder = 'Select the new part number AI';
    public readonly newPartNumberCypressTag = 'pnc-ai-solution-new-part-number';
    public readonly quantityLabel = 'Quantity';
    public readonly quantityPlaceholder = 'e.g. 1';
    public readonly quantityCypressTag = 'pnc-ai-solution-new-part-number-quantity';
    public readonly additionalInfoLabel = 'Additional information';
    public readonly additionalInfoPlaceholder = 'Write an additional information';
    public readonly additionalInfoCypressTag = 'pnc-ai-solution-additional-information';

    private subscriptions = new Subscription();

    get hasDisplayConfiguration(): boolean {
        return this.configuration != null;
    }

    get globalStyleClass(): string {
        let styleClass = 'pnc-solution';

        if (this.configuration?.displayConfiguration?.displayMode != null) {
            styleClass += ` ${this.configuration?.displayConfiguration.displayMode}`;
        }

        return styleClass;
    }

    get showInViewMode(): boolean {
        return this.configuration?.displayConfiguration.displayMode === PartNumberChangesDetailTrialDisplayModeEnum.DETAIL;
    }

    get containerTypeClass(): string {
        return this.showInViewMode ? 'view' : null;
    }

    get materialInputFormatter(): (x: { number: string, description: string }) => string {
        if (this.showInViewMode) {
            return this.typeahead.inputTextNumberDescriptionFormatter;
        }
        return this.typeahead.inputTextNumberVariationDescriptionFormatter;
    }

    constructor(public typeahead: TypeaheadHelperService,
                private spinnerService: SpinnerService,
                private materialApi: MaterialApi,
                private clipboard: Clipboard,
                private toast: ToastWrapperService) {
        super();
    }

    ngOnInit(): void {
        this.storeState({
            material: undefined,
            quantity: undefined,
            isValid: false,
            supplyingPackingPlantCode: this.supplyingPackingPlantCode,
            oldPartNumberMaterialPrefix: null,
            oldPartNumberMaterialCode: null,
            newPartNumberMaterials: []
        });

        if (this.updatedOldPartNumberMaterial != null) {
            this.setUpdatedOldPartNumberMaterialSubscription();
        }
    }

    ngAfterViewInit(): void {
        this.toggleInputs(true);
    }

    searchMaterials = (term: string): Observable<MaterialDetailContract[]> => {
        if (term?.length > 0) {
            return of(this.state.newPartNumberMaterials.filter((item) => (`${item.number} - ${item.description}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.state.newPartNumberMaterials);
    };

    getAllActiveMaterials(term: string, plant: string): Observable<MaterialDetailContract[]> {
        let materials = of([]);
        if (plant != null && term?.length > 0) {
            // maxResults = 100 to ensure that all active materials are returned.
            // Unlikely, there aren't these much results because we are already filtering by one specific Part Number Code
            // and what is returned are the Part Number Code variations
            materials = this.materialApi.getAllAiActiveMaterials(term, plant, 0, 100)
                    .pipe(map(MaterialDetailMapper.fromMaterialDetailContractArray));
        }

        return materials;
    }

    materialChange(event: MaterialDetailContract | string): void {
        this.storeState({
            ...this.state,
            material: event
        });

        this.manageMaterialMessages();
    }

    quantityChange(event: string): void {
        this.storeState({
            ...this.state,
            quantity: event
        });

        this.manageQuantityMessages();
    }

    /**
     * Sets the new problem description value
     */
    setAdditionalInformation(additionalInformation: string): void {
        this.storeState({additionalInformation: additionalInformation});
    }

    storeState(state?: Partial<PartNumberChangesAiSolutionState>): void {
        super.storeState(state);
        super.storeState({isValid: this.isMaterialValid(this.state.material) && this.isQuantityValid(this.state.quantity)});
        this.solution.next(this.state);
    }

    initialValuePipe(displayMode: InputDisplayModeEnum, additionalInformation: string): string {
        let pipeValue = additionalInformation;
        if (displayMode === InputDisplayModeEnum.VIEW && additionalInformation == null) {
            pipeValue = '-';
        }
        return pipeValue;
    }

    quantityInitialValuePipe(cellValue: any): string {
        let value = '-';

        if (cellValue != null || !this.showInViewMode) {
            value = cellValue;
        }

        return value;
    }

    warningLevel(required: boolean): WarningsEnum {
        return required ? WarningsEnum.WARNING_PNC_SEND : null;
    }

    copyMaterialNumber(): void {
        let copyValue = null;

        if (this.state.material?.constructor === Object.prototype.constructor) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const material: MaterialDetailContract = <MaterialDetailContract> this.state.material;
            copyValue = material?.number ?? null;

            if (copyValue != null) {
                this.clipboard.copy(copyValue);
                this.toast.success(`The part number ${copyValue} was successfully copied to clipboard`, 'Success');
            }
        }
    }

    private setUpdatedOldPartNumberMaterialSubscription(): void {

        this.subscriptions.add(this.updatedOldPartNumberMaterial.subscribe((updateAiMaterial: PartNumberChangesUpdateAiMaterialModel) => {

            if (updateAiMaterial.materialUnavailable) {
                this.toggleInputs(true);
                this.materialInput.markAsInvalid();
                this.materialMessages.set(WarningsEnum.ERROR, ['This material is not available in the selected packing plant']);
            } else if (updateAiMaterial.oldMaterialFieldDisabled) {
                this.toggleInputs(true);
            } else {
                if (updateAiMaterial.material == null) {
                    // Clean the field if the oldPartNumberMaterialCode was deleted
                    this.storeState({oldPartNumberMaterialPrefix: null});
                    this.materialInput.deleteInputValue();
                    this.toggleInputs(true);
                } else if (this.isMaterialValid(updateAiMaterial.material)) {
                    const formattedNumber = `${updateAiMaterial.material?.number?.split('-')[0]}-`;
                    this.manageNewPartNumberMaterials(updateAiMaterial.material.number,
                            updateAiMaterial.material?.supplyingPackingPlantCode);
                    this.storeState(
                            {
                                oldPartNumberMaterialPrefix: formattedNumber,
                                oldPartNumberMaterialCode: updateAiMaterial.material?.number
                            });
                }
            }
        }));
    }

    private manageNewPartNumberMaterials(code: string, spp: string): void {
        if (code !== this.state.oldPartNumberMaterialCode) {
            this.isLoading?.(true);
            this.subscriptions.add(this.getAllActiveMaterials(code, spp)
                    .subscribe((newMaterials) => {
                        this.materialInput.deleteInputValue();
                        // Update newPartNumberMaterials based on new SPPCode
                        this.storeState({newPartNumberMaterials: newMaterials});

                        this.manageNewPartNumberDropdown(newMaterials);
                    })
                    .add(() => {
                        this.isLoading?.(false);
                    })
            );
        } else {
            this.manageNewPartNumberDropdown(this.state.newPartNumberMaterials);
        }
    }

    private manageNewPartNumberDropdown(newPartNumberMaterials: MaterialDetailContract[]): void {
        if (newPartNumberMaterials.length === 0) {
            this.toggleInputs(true);
            this.materialMessages.set(WarningsEnum.WARNING,
                    ['There is no alternative active AI level for the selected old part number/material']);
        } else if (newPartNumberMaterials.length === 1) {
            this.materialInput.setValue(newPartNumberMaterials[0]);
            this.quantityInput?.enable();
            this.materialInput.disable();
        } else {
            this.toggleInputs(false);
        }
    }

    private manageMaterialMessages(): void {
        const messages = new Map<WarningsEnum, string[]>();

        if (this.materialInput?.inputControl?.value == null || this.materialInput?.inputControl?.value.length === 0) {
            this.addInputMessage(messages, WarningsEnum.WARNING, 'Mandatory field to send');
        } else if (this.materialInput?.inputControl?.invalid === true) {
            this.addInputMessage(messages, WarningsEnum.ERROR, 'Please enter a valid material');
        }

        this.materialMessages = messages;
    }

    private manageQuantityMessages(): void {
        const messages = new Map<WarningsEnum, string[]>();

        if ((this.quantityInput?.inputControl?.value == null || this.quantityInput?.inputControl?.value.length === 0)
                && this.configuration?.displayConfiguration?.quantityInput?.required) {
            this.addInputMessage(messages, WarningsEnum.WARNING, 'Mandatory field to send');
        } else if (this.quantityInput?.inputControl?.invalid === true) {
            this.addInputMessage(messages, WarningsEnum.ERROR, 'Please enter a valid quantity, e.g. 1');
        }

        this.quantityMessages = messages;
    }

    private addInputMessage = (enumMap: Map<WarningsEnum, string[]>, level: WarningsEnum, message: string): void => {
        if (enumMap != null && level != null && message != null) {
            if (enumMap.has(level)) {
                enumMap.get(level)
                        .push(message);
            } else {
                enumMap.set(level, [message]);
            }
        }
    };

    private isMaterialValid(material: MaterialDetailContract | string): boolean {
        return material != null && typeof material !== 'string'
                && material?.number !== null && material?.number !== ''
                && material?.description !== null && material?.description !== ''
                && material?.salesUnit !== null && material?.salesUnit !== ''
                && material?.supplyingPackingPlantCode !== null && material?.supplyingPackingPlantCode !== ''
                && material?.active !== null;
    }

    private isQuantityValid(quantity: string): boolean {
        let valid = true;
        if (quantity !== '' && quantity != null) {
            valid = new RegExp(this.quantityRegex)
                    .test(quantity);
        }
        return valid;
    }

    private toggleInputs(disabled: boolean): void {
        if (disabled) {
            this.materialInput?.disable();
            this.quantityInput?.disable();
        } else {
            this.materialInput?.enable();
            this.quantityInput?.enable();
        }

        this.materialMessages.delete(WarningsEnum.WARNING);
        this.quantityMessages.delete(WarningsEnum.WARNING);
    }
}
